/* eslint-disable react-hooks/refs -- false positive: prop forwarding, not ref.current reads */
import {Box, HStack, Show} from 'platform/foundation';
import styled, {css} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {KeyboardEvent, ReactNode, useEffect, useId, useRef, useState} from 'react';

import {always} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps, useDimensions, useQueryState} from 'shared/web';

import {Actions} from '../Actions/Actions';
import {Separator} from '../Separator/Separator';
import {Tab, TabProps} from './Tab';
import {determineLazyBehavior} from './utils/determineLazyBehavior';

const SEARCH_QUERY_KEY = 'controlled-tab';

export type TabsProps = TestIdProps & {
  variant?: 'container' | 'condensed' | 'fullHeight' | 'default';
  tabs: TabProps[];
  isFullWidth?: boolean;
  children?: ReactNode;
  shouldWrap?: boolean;
  isLazy?: boolean;
  isFullHeight?: boolean;
  hasSeparator?: boolean;
  lazyBehavior?: 'unmount' | 'keepMounted';
} & (
    | {
        activeTabId: string | Nullish;
        onChange: (id: string) => void;
        defaultTabId?: never;
      }
    | {
        activeTabId?: never;
        onChange?: never;
        defaultTabId?: string;
      }
  );

export function Tabs(props: TabsProps) {
  const queryId = useId();
  const tabIdPrefix = useId();
  const panelIdPrefix = useId();
  const [tabListRef, tabListSize] = useDimensions();
  const hasBeenSelected = useRef<Record<string, boolean>>({});
  const [innerActiveTabId, setInnerActiveTabId] = useQueryState(`${queryId}-${SEARCH_QUERY_KEY}`);

  // Track focused tab separately from active tab
  const [focusedTabId, setFocusedTabId] = useState<string | Nullish>(null);

  const activeTabId =
    props.activeTabId ?? innerActiveTabId ?? props.defaultTabId ?? props.tabs[0]?.id;
  const setActiveTabId = props.onChange ?? setInnerActiveTabId;
  const activeTabActions = props.tabs.find((item) => item.id === activeTabId)?.actions;

  // Clear stale focus state when active tab changes programmatically
  useEffect(() => {
    if (focusedTabId && focusedTabId !== activeTabId) {
      setFocusedTabId(null);
    }
  }, [activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isNilOrEmpty(props.tabs)) {
    return null;
  }

  const handleChangeTab = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const handleFocusTab = (tabId: string) => {
    setFocusedTabId(tabId);
  };

  const getTabId = (tabId: string) => `${tabIdPrefix}-tab-${tabId}`;
  const getPanelId = (tabId: string) => `${panelIdPrefix}-panel-${tabId}`;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabledTabs = props.tabs.filter((tab) => !tab.isDisabled);
    const currentFocusedTab = focusedTabId ?? activeTabId;
    const currentIndex = enabledTabs.findIndex((tab) => tab.id === currentFocusedTab);

    if (currentIndex === -1) {
      return;
    }

    const focusTab = (tabId: string) => {
      const tabElement = document.getElementById(getTabId(tabId));
      if (tabElement) {
        tabElement.focus();
      }
    };

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? enabledTabs.length - 1 : currentIndex - 1;
        const prevTab = enabledTabs.at(prevIndex);
        if (prevTab && prevIndex >= 0 && prevIndex < enabledTabs.length) {
          handleFocusTab(prevTab.id);
          focusTab(prevTab.id);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        const nextIndex = currentIndex === enabledTabs.length - 1 ? 0 : currentIndex + 1;
        const nextTab = enabledTabs.at(nextIndex);
        if (nextTab && nextIndex >= 0 && nextIndex < enabledTabs.length) {
          handleFocusTab(nextTab.id);
          focusTab(nextTab.id);
        }
        break;
      case 'Home':
        event.preventDefault();
        const firstTab = enabledTabs.at(0);
        if (firstTab) {
          handleFocusTab(firstTab.id);
          focusTab(firstTab.id);
        }
        break;
      case 'End':
        event.preventDefault();
        const lastTab = enabledTabs.at(-1);
        if (lastTab) {
          handleFocusTab(lastTab.id);
          focusTab(lastTab.id);
        }
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        if (currentFocusedTab) {
          handleChangeTab(currentFocusedTab);
          // Update focus to match the newly activated tab
          setFocusedTabId(currentFocusedTab);
        }
        break;
    }
  };

  return (
    <>
      <div ref={tabListRef}>
        <HStack justify="space-between" spacing={2} wrap>
          <StyledTabList
            role="tablist"
            onKeyDown={handleKeyDown}
            $isFullWidth={props.isFullWidth}
            $shouldWrap={props.shouldWrap}
            $variant={props.variant}
          >
            {props.tabs.map((tab) => (
              <Tab
                key={tab.id}
                id={tab.id}
                title={tab.title}
                icon={tab.icon}
                badge={tab.badge}
                variant={props.variant}
                isDisabled={tab.isDisabled}
                isSelected={tab.id === activeTabId}
                isFocused={tab.id === focusedTabId}
                isFullWidth={props.isFullWidth}
                onClick={handleChangeTab}
                onFocus={handleFocusTab}
                data-testid={tab['data-testid']}
                data-tab-id={tab.id}
                tabId={getTabId(tab.id)}
                panelId={getPanelId(tab.id)}
                tabIndex={tab.id === activeTabId ? 0 : -1}
              />
            ))}
          </StyledTabList>
          <Actions actions={activeTabActions} />
        </HStack>
        <Show when={props.hasSeparator}>
          <Separator spacing={0} />
        </Show>
      </div>
      <Box
        height={props.isFullHeight ? `calc(100% - ${tabListSize.height}px)` : undefined}
        width="100%"
      >
        {props.tabs.map((tab) => {
          const isTabActive = tab.id === activeTabId;

          if (isTabActive) {
            hasBeenSelected.current[tab.id] = true;
          }

          const shouldRenderTab = determineLazyBehavior({
            hasBeenSelected: hasBeenSelected.current[tab.id],
            isSelected: isTabActive,
            isLazy: props.isLazy,
            lazyBehavior: props.lazyBehavior,
          });

          if (!shouldRenderTab) {
            return null;
          }

          return (
            <StyledTabPanel
              $variant={props.variant}
              key={tab.id}
              role="tabpanel"
              id={getPanelId(tab.id)}
              aria-labelledby={getTabId(tab.id)}
              aria-hidden={!isTabActive}
              hidden={!isTabActive}
              tabIndex={-1}
              $isFullHeight={props.isFullHeight}
              data-testid={suffixTestId('tabPanel', {'data-testid': tab['data-testid']})}
            >
              {tab.content}
            </StyledTabPanel>
          );
        })}
      </Box>
    </>
  );
}

export type StyledTabListProps = {
  $isFullWidth?: TabsProps['isFullWidth'];
  $shouldWrap?: TabsProps['shouldWrap'];
  $variant?: TabsProps['variant'];
};

export const StyledTabList = styled.div<StyledTabListProps>`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  flex: ${(props) =>
    match([props.$isFullWidth, props.$shouldWrap])
      .with([true, Pattern.any], always('100%'))
      .with([Pattern.any, true], always('auto'))
      .otherwise(always('none'))};
  flex-wrap: ${(props) => (props.$shouldWrap ? 'wrap' : 'nowrap')};
  white-space: ${(props) => props.$shouldWrap && 'nowrap'};
  column-gap: ${({theme, $variant}) => ($variant !== 'container' ? theme.getSize(6) : undefined)};
  row-gap: ${({theme, $variant}) => ($variant !== 'container' ? theme.getSize(2) : undefined)};
`;

export type StyledTabPanelProps = {
  $variant?: TabsProps['variant'];
  $isFullHeight?: TabsProps['isFullHeight'];
};

const getVariantStyles = ($variant?: TabsProps['variant']) => {
  switch ($variant) {
    case 'container':
      return css<StyledTabPanelProps>`
        padding-left: 16px;
        padding-right: 16px;
      `;
    case 'fullHeight':
      return css<StyledTabPanelProps>`
        height: 100%;
        padding-bottom: 0;
      `;
    default:
      return css<StyledTabPanelProps>``;
  }
};

const StyledTabPanel = styled.div<StyledTabPanelProps>`
  height: ${(props) => (props.$isFullHeight ? '100%' : undefined)};
  width: 100%;
  padding-inline-start: 0;
  padding-inline-end: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  ${({$variant}) => getVariantStyles($variant)}
`;
